import React, { useMemo, useState } from 'react';
import { CheckCircle, X } from 'lucide-react';
import { SaveAsTemplateModal } from '../../components/SaveAsTemplateModal';
import { ComponentLibraryModal } from '../../components/ComponentLibraryModal';
import { LibraryComponentEditor } from '../../components/LibraryComponentEditor';
import { HeadContentEditModal } from '../../components/HeadContentEditModal';
import { BodyEndEditModal } from '../../components/BodyEndEditModal';
import { EditPageModal } from '../../components/EditPageModal';
import { ConfirmModal } from '../../components/ConfirmModal';
import { ResponsivePreviewPanel } from '../../components/ResponsivePreviewPanel';
import { SEOAnalysisPanel } from '../../components/SEOAnalysisPanel';
import { INVISIBLE_COMPONENT_TYPES } from '../../components/InvisibleComponentsBar';
import type { ToastType, ToastOptions } from '../../components/Toast';
import { layoutsApi, pagesApi } from '../../api/client';
import type {
  Page,
  Site,
  Component,
  LibraryComponent,
  ComponentType,
  PageLayout,
} from '../../schemas/validation';
import {
  applyComponentEditDraftToComponents,
  useComponentEditDraft,
} from '../../contexts/ComponentEditDraftContext';

interface PageEditorModalsProps {
  siteId: string;
  pageId: string;
  site: Site;
  currentPage: Page;
  components: Component[];
  isAdmin: boolean;

  // Modal visibility states
  showSaveTemplateModal: boolean;
  showComponentLibraryModal: boolean;
  showHeadContentModal: boolean;
  showBodyEndModal: boolean;
  showEditPageModal: boolean;
  showResponsivePreview: boolean;
  showSEOAnalysis: boolean;
  showSuccessToast: boolean;
  successMessage: string;

  // Loading states
  isSavingTemplate: boolean;
  isAddingComponent: boolean;

  // Other states
  isInvisibleComponentMode: boolean;
  editingInvisibleComponent: Component | null;
  insertPosition: number | null;
  componentRefreshTrigger: number;

  // Modal states for complex modals
  duplicateModal: { isOpen: boolean; type: string; comps: Component[] };
  deleteModal: { isOpen: boolean; component: Component | null };
  libraryEditorModal: { isOpen: boolean; component: LibraryComponent | null };

  // Handlers
  onCloseSaveTemplateModal: () => void;
  onSaveAsTemplate: (data: {
    name: string;
    description?: string;
    tags?: string;
    isPublic: boolean;
  }) => Promise<void>;
  onCloseComponentLibraryModal: () => void;
  onSelectTemplate: (componentType: ComponentType) => void;
  onPlaceCompeteForm?: (form: {
    id: string;
    name: string;
    shareToken: string;
    desktopHeight: number;
    mobileHeight: number;
  }) => void;
  onSelectLibraryComponent: (libraryComponentId: string) => void;
  onEditLibraryComponent: (component: LibraryComponent) => void;
  onCloseLibraryEditorModal: () => void;
  onLibrarySaveSuccess: () => void;
  onLibrarySyncSuccess: () => void;
  onCloseHeadContentModal: () => void;
  onSaveHeadContent: (data: any) => Promise<void>;
  onCloseBodyEndModal: () => void;
  onSaveBodyEnd: (data: any) => Promise<void>;
  onCloseEditPageModal: () => void;
  onPageUpdated: (page: Page) => void;
  onUnpublish?: (page: Page) => void;
  onCloseDuplicateModal: () => void;
  onDuplicateCleanup: () => Promise<void>;
  onCloseDeleteModal: () => void;
  onConfirmDelete: () => Promise<void>;
  onCloseResponsivePreview: () => void;
  onCloseSEOAnalysis: () => void;
  onSeoFixApplied?: () => void;
  onCloseSuccessToast: () => void;
  setCurrentPage: (page: Page) => void;
  setPageLayout: (layout: PageLayout | null) => void;
  showToast: (message: string, type?: ToastType, options?: string | ToastOptions) => void;
  onPublishPage?: () => Promise<void>;
  isPublishingPage?: boolean;
  onRefetchPublishStatus?: () => Promise<void>;
}

export const PageEditorModals: React.FC<PageEditorModalsProps> = ({
  siteId,
  pageId,
  site,
  currentPage,
  components,
  isAdmin,

  showSaveTemplateModal,
  showComponentLibraryModal,
  showHeadContentModal,
  showBodyEndModal,
  showEditPageModal,
  showResponsivePreview,
  showSEOAnalysis,
  showSuccessToast,
  successMessage,

  isSavingTemplate,
  isAddingComponent,

  isInvisibleComponentMode,
  editingInvisibleComponent,
  insertPosition: _insertPosition,
  componentRefreshTrigger,

  duplicateModal,
  deleteModal,
  libraryEditorModal,

  onCloseSaveTemplateModal,
  onSaveAsTemplate,
  onCloseComponentLibraryModal,
  onSelectTemplate,
  onPlaceCompeteForm,
  onSelectLibraryComponent,
  onEditLibraryComponent,
  onCloseLibraryEditorModal,
  onLibrarySaveSuccess,
  onLibrarySyncSuccess,
  onCloseHeadContentModal,
  onSaveHeadContent,
  onCloseBodyEndModal,
  onSaveBodyEnd,
  onCloseEditPageModal,
  onPageUpdated: _onPageUpdated,
  onUnpublish,
  onCloseDuplicateModal,
  onDuplicateCleanup,
  onCloseDeleteModal,
  onConfirmDelete,
  onCloseResponsivePreview,
  onCloseSEOAnalysis,
  onSeoFixApplied,
  onCloseSuccessToast,
  setCurrentPage,
  setPageLayout,
  showToast,
  onPublishPage,
  isPublishingPage = false,
  onRefetchPublishStatus,
}) => {
  const [showLayoutRepublishPrompt, setShowLayoutRepublishPrompt] = useState(false);

  const handlePageUpdated = async (
    updatedPage: Page,
    meta?: { layoutSettingsChanged?: boolean }
  ) => {
    let pageForState = updatedPage;

    if (meta?.layoutSettingsChanged) {
      try {
        pageForState = await pagesApi.get(updatedPage.id);
      } catch (err) {
        console.error('Failed to refresh page after layout settings change:', err);
      }
    }

    setCurrentPage(pageForState);
    onCloseEditPageModal();
    await onRefetchPublishStatus?.();

    showToast('Page settings updated successfully!', 'success', {
      showRoary: true,
      phraseKey: 'changes_saved',
    });

    if (meta?.layoutSettingsChanged && pageForState.publishedAt) {
      setShowLayoutRepublishPrompt(true);
    }

    // Reload layout if it changed
    if (pageForState.showLayout !== false) {
      try {
        if (pageForState.layoutId) {
          // Specific layout assigned
          const layout = await layoutsApi.get(pageForState.layoutId);
          setPageLayout(layout);
        } else {
          // No specific layout — use site default (same rule as live rendering)
          const defaultResult = await layoutsApi.getDefault(siteId);
          setPageLayout(defaultResult.layout || null);
        }
      } catch (err) {
        console.error('Failed to load updated layout:', err);
        setPageLayout(null);
      }
    } else {
      setPageLayout(null);
    }
  };

  const editDraft = useComponentEditDraft()?.draft ?? null;
  const previewComponents = useMemo(
    () => applyComponentEditDraftToComponents(components, editDraft),
    [components, editDraft]
  );

  return (
    <>
      {/* Save as Template Modal */}
      <SaveAsTemplateModal
        isOpen={showSaveTemplateModal}
        onClose={onCloseSaveTemplateModal}
        onSubmit={onSaveAsTemplate}
        isLoading={isSavingTemplate}
        pageTitle={currentPage.title}
        isAdmin={isAdmin}
      />

      {/* Component Library Modal */}
      <ComponentLibraryModal
        isOpen={showComponentLibraryModal}
        onClose={onCloseComponentLibraryModal}
        onSelectTemplate={onSelectTemplate}
        onPlaceCompeteForm={onPlaceCompeteForm}
        onSelectLibraryComponent={onSelectLibraryComponent}
        onEditLibraryComponent={onEditLibraryComponent}
        isLoading={isAddingComponent}
        siteId={siteId}
        allowedCategories={isInvisibleComponentMode ? ['Technical'] : undefined}
        existingComponentTypes={[
          ...(isInvisibleComponentMode
            ? components.filter(c => INVISIBLE_COMPONENT_TYPES.includes(c.type)).map(c => c.type)
            : []),
          ...(components.some(c => c.type === 'conversion-popup')
            ? (['conversion-popup'] as ComponentType[])
            : []),
        ]}
      />

      {/* Library Component Editor Modal */}
      <LibraryComponentEditor
        isOpen={libraryEditorModal.isOpen}
        onClose={onCloseLibraryEditorModal}
        libraryComponent={libraryEditorModal.component}
        onSaveSuccess={onLibrarySaveSuccess}
        onSyncSuccess={onLibrarySyncSuccess}
      />

      {/* Head Content Edit Modal */}
      <HeadContentEditModal
        isOpen={showHeadContentModal}
        onClose={onCloseHeadContentModal}
        onSave={onSaveHeadContent}
        initialData={editingInvisibleComponent?.data}
        isLoading={isAddingComponent}
        siteId={siteId}
        siteOgImage={site.ogImage}
      />

      {/* Body End Edit Modal */}
      <BodyEndEditModal
        isOpen={showBodyEndModal}
        onClose={onCloseBodyEndModal}
        onSave={onSaveBodyEnd}
        initialData={editingInvisibleComponent?.data as any}
        isLoading={isAddingComponent}
      />

      {/* Edit Page Modal */}
      {currentPage && (
        <EditPageModal
          isOpen={showEditPageModal}
          onClose={onCloseEditPageModal}
          page={currentPage}
          onPageUpdated={handlePageUpdated}
          onUnpublish={onUnpublish}
        />
      )}

      <ConfirmModal
        isOpen={showLayoutRepublishPrompt}
        onClose={() => setShowLayoutRepublishPrompt(false)}
        onConfirm={async () => {
          setShowLayoutRepublishPrompt(false);
          if (onPublishPage) {
            await onPublishPage();
          }
        }}
        title="Republish to update live site"
        message="You changed this page's layout settings. The editor and preview already reflect that, but visitors won't see it on the live site until you republish."
        confirmText="Republish now"
        cancelText="Not now"
        type="warning"
        isLoading={isPublishingPage}
        loadingText="Publishing..."
      />

      {/* Duplicate Components Modal */}
      <ConfirmModal
        isOpen={duplicateModal.isOpen}
        onClose={onCloseDuplicateModal}
        onConfirm={onDuplicateCleanup}
        title="Duplicate Components Detected"
        message={`This page has ${duplicateModal.comps.length} "${
          duplicateModal.type === 'head-content'
            ? 'SEO & Meta Tags'
            : duplicateModal.type === 'body-end'
              ? 'Scripts & Widgets'
              : duplicateModal.type
        }" components. Would you like to keep only the most recent one and delete the duplicates?`}
        confirmText="Remove Duplicates"
        cancelText="Keep All"
        type="warning"
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={onCloseDeleteModal}
        onConfirm={onConfirmDelete}
        title="Delete Component"
        message={`Are you sure you want to delete the ${
          deleteModal.component?.type === 'head-content'
            ? 'SEO & Meta Tags'
            : deleteModal.component?.type === 'body-end'
              ? 'Scripts & Widgets'
              : deleteModal.component?.type
        } component?`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Responsive Preview Panel */}
      <ResponsivePreviewPanel
        siteId={siteId}
        pageId={pageId}
        isOpen={showResponsivePreview}
        onClose={onCloseResponsivePreview}
        refreshTrigger={componentRefreshTrigger}
        components={previewComponents}
      />

      {/* SEO Analysis Panel */}
      {pageId && (
        <SEOAnalysisPanel
          pageId={pageId}
          isOpen={showSEOAnalysis}
          onClose={onCloseSEOAnalysis}
          onFixApplied={onSeoFixApplied}
          title={`Improvements and SEO - ${currentPage?.title || 'Page'}`}
          siteOgImage={site.ogImage}
          onAddComponent={onSelectTemplate}
        />
      )}

      {/* Success Toast (Legacy - for template save) */}
      {showSuccessToast && (
        <div className="fixed top-4 right-4 z-[70]">
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 max-w-sm animate-slide-in-right">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">Success!</p>
                <p className="text-sm text-gray-600 mt-1">{successMessage}</p>
              </div>
              <button
                onClick={onCloseSuccessToast}
                className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
